import { useQuery } from '@apollo/client';
import { useState } from 'react';
import { Key } from 'react-aria-components';

import { type ExchangeRateEntry, exchangeRateFor, QUERY_EXCHANGE_RATES } from 'src/api/exchangeRates.api';
import { useFieldCommit } from 'src/hooks/useFieldCommit';
import type { OrganizationExchangeRatesQuery, OrganizationExchangeRatesQueryVariables } from 'types/graphql';
import { AgInput } from './AgInput';
import { AgSelect } from './AgSelect';
import { cn } from './utils';

const currencyOptions = [
  { value: 'USD', label: 'USD' },
  { value: 'CAD', label: 'CAD' },
];

export interface AgCurrencyInputProps {
  /** Current currency value */
  currencyValue?: string;
  /** Current exchange rate value */
  exchangeRateValue?: number;
  /** Whether the inputs are disabled */
  isDisabled?: boolean;
  /** Show red asterisk for required fields */
  isRequired?: boolean;
  /** Callback when currency changes */
  onChangeCurrency?: (value: string) => void;
  /** Callback when exchange rate changes */
  onChangeExchangeRate?: (value: number) => void;
  /** Additional class for the container */
  className?: string;
}

export function AgCurrencyInput({
  currencyValue,
  exchangeRateValue,
  isDisabled,
  isRequired,
  onChangeCurrency,
  onChangeExchangeRate,
  className,
}: AgCurrencyInputProps) {
  const { data } = useQuery<OrganizationExchangeRatesQuery, OrganizationExchangeRatesQueryVariables>(
    QUERY_EXCHANGE_RATES,
    { fetchPolicy: 'cache-first' }
  );

  const [exchangeRate, setExchangeRate] = useState<ExchangeRateEntry>(exchangeRateValue ?? 1);

  const currency = currencyValue ?? 'USD';
  const rates = data?.exchangeRates?.rates;
  const suggestedRate = exchangeRateFor(rates, currency);

  const handleCurrencyChange = (key: Key) => {
    const value = String(key);
    onChangeCurrency?.(value);

    const newRate = exchangeRateFor(rates, value);
    if (newRate) {
      setExchangeRate(newRate);
      onChangeExchangeRate?.(Number(newRate));
    }
  };

  // Committing a non-positive rate falls back to the suggested rate.
  const rateField = useFieldCommit<string>({
    value: String(exchangeRate),
    onCommit: (value) => {
      const numValue = Number(value);

      if (numValue > 0) {
        setExchangeRate(numValue);
        onChangeExchangeRate?.(numValue);
        return;
      }

      if (suggestedRate) {
        setExchangeRate(Number(suggestedRate));
        onChangeExchangeRate?.(Number(suggestedRate));
      }
    },
  });

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <span className="text-14 font-medium text-neutral-text">
        Exchange Rate
        {isRequired && <span className="text-red-text ml-2">*</span>}
      </span>
      <div className="flex">
        <AgSelect
          value={currency}
          onChange={handleCurrencyChange}
          isDisabled={isDisabled}
          options={currencyOptions}
          className="w-[100px]"
          triggerClassName="rounded-r-none border-r-0"
        />
        <AgInput
          isDisabled={isDisabled || currency === 'USD'}
          type="number"
          wrapperClassName="rounded-l-none"
          className="flex-1"
          {...rateField}
        />
      </div>
    </div>
  );
}

AgCurrencyInput.displayName = 'AgCurrencyInput';
